import {
  ORANGE, TEAL, VERT, formatPrix, Avatars, TourneeBar,
  producersOf, cheapestOffer, deriveRayons, rayonOf,
} from './catalogue-utils'
import PaniersCarousel from './PaniersCarousel'
import { useTheme } from '../../theme'
import { avecAlpha } from '../../lib/couleurs'

// Ecran « Rayons » du module Paniers (client) — fidele prototype v5 :
// bandeau tournee + selecteur de rayons (pastilles) + grille de produits
// (avatars producteurs, badge multi-offres, ajout direct). Touch sur la carte
// -> fiche produit (ProduitScreen).

export default function CatalogueScreen({
  glass, tc, ts,
  tour, produits, paniers = [], loadingDetail,
  rayon, setRayon, cart, formatDate, onOpenProduit,
}) {
  const { isWeb3 } = useTheme()
  if (loadingDetail) {
    return (
      <div className="grid grid-cols-2 gap-3 mt-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-40 rounded-2xl animate-pulse bg-ink/5`} />
        ))}
      </div>
    )
  }

  // Rien du tout (ni paniers, ni produits) : etat vide global.
  if (produits.length === 0 && paniers.length === 0) {
    return (
      <>
        <TourneeBar tour={tour} formatDate={formatDate} />
        <div className="text-center pt-8 animate-fade-up">
          <span className="text-4xl block mb-2">🧺</span>
          <p className={`text-sm font-semibold ${tc}`}>Aucun produit disponible</p>
          <p className={`text-xs mt-1 ${ts}`}>Les producteurs ajoutent leurs offres, reviens bientôt.</p>
        </div>
      </>
    )
  }

  const rayons = deriveRayons(produits)
  const visibles = rayon === 'Tout' ? produits : produits.filter((p) => rayonOf(p) === rayon)
  const cartMap = cart.cartFor(tour.id)

  return (
    <>
      <TourneeBar tour={tour} formatDate={formatDate} />

      {/* Paniers precomposes de la semaine (carousel) */}
      <PaniersCarousel paniers={paniers} glass={glass} tc={tc} ts={ts} />

      {/* Produits a composer soi-meme */}
      {paniers.length > 0 && produits.length > 0 && (
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] mb-2.5 mx-0.5" style={{ color: ORANGE }}>
          Ou composez le vôtre
        </p>
      )}

      {produits.length === 0 ? (
        <p className={`text-center text-xs py-4 ${ts}`}>Aucun produit à composer pour cette tournée.</p>
      ) : (
      <>
      {/* Selecteur de rayons (pastilles, scroll horizontal) */}
      <div className="-mx-4 px-4 mb-3">
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
          {rayons.map((r) => {
            const active = rayon === r.n
            return (
              <button
                key={r.n}
                onClick={() => setRayon(r.n)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-full text-[13.5px] font-bold transition-colors ${active ? '' : `${glass} ${tc}`}`}
                style={active ? { background: VERT, color: 'rgb(var(--c-creme))' } : undefined}
              >
                <span className="text-base">{r.e}</span>
                {r.n}
              </button>
            )
          })}
        </div>
      </div>

      {/* Grille produits du rayon selectionne */}
      <div className="grid grid-cols-2 gap-3">
        {visibles.map((p, i) => {
          const off = cheapestOffer(p)
          const out = !off
          const producers = producersOf(p)
          const multi = producers.length > 1 && !out
          const qty = off ? (cartMap[off.tourOfferId] || 0) : 0
          return (
            <div
              key={p.product?.id || i}
              className={`relative rounded-[20px] p-3.5 animate-fade-up ${glass} ${out ? 'opacity-55' : ''}`}
              style={{
                animationDelay: `${i * 0.03}s`,
                ...(multi
                  ? { boxShadow: '5px 7px 0 -2px rgba(255,255,255,0.55), 5px 7px 0 -1px rgba(7,62,61,0.10), 10px 13px 0 -4px rgba(255,255,255,0.4), 10px 13px 0 -3px rgba(7,62,61,0.07)' }
                  : {}),
              }}
            >
              {multi && (
                <span className="absolute top-2.5 right-2.5 z-[2] text-[8.5px] font-extrabold tracking-wide px-2 py-0.5 rounded-full" style={{ background: 'rgba(7,62,61,0.85)', color: 'rgb(var(--c-creme))' }}>
                  {p.offres.length} OFFRES
                </span>
              )}
              <button
                onClick={() => onOpenProduit(p)}
                className="block w-full text-left p-0 bg-transparent border-0"
              >
                <div className="text-[40px] text-center pt-1 pb-0.5" style={out ? { filter: 'grayscale(1)' } : undefined}>
                  {p.product?.category?.emoji || '🧺'}
                </div>
                <p className={`text-sm font-bold ${tc}`}>{p.product?.nom || 'Produit'}</p>
                {!out && producers.length > 0 && (
                  <div className="mt-1.5 mb-0.5"><Avatars producers={producers} /></div>
                )}
                <p className={`text-[11px] ${ts}`}>{out ? 'Épuisé pour cette tournée' : (off.unit || p.product?.unit || '')}</p>
                <p className={`text-[16px] font-extrabold mt-1 ${tc}`}>{out ? '—' : formatPrix(p.prixMin ?? off.prix)}</p>
              </button>
              {!out && (
                <button
                  onClick={() => cart.add(tour.id, off.tourOfferId, 1)}
                  className="absolute right-2.5 bottom-2.5 w-[38px] h-[38px] rounded-[14px] flex items-center justify-center text-[18px] font-bold"
                  style={{ background: qty ? TEAL : ORANGE, color: VERT, boxShadow: `0 8px 18px ${avecAlpha(ORANGE, 0.35)}` }}
                  aria-label="Ajouter au panier"
                >
                  {qty || '+'}
                </button>
              )}
            </div>
          )
        })}
      </div>
      </>
      )}
    </>
  )
}
